import { useCallback, useEffect, useRef, type RefObject } from "react";
import { useLockStore } from "../../store/lockStore";
import { lockApi } from "../../api/lockApi";
import { asError, eventBus } from "@optimyl/events";
import { ACCESS_GRANTED_DISPLAY_MS } from "./constants";

interface GrantTransition {
  /**
   * Show the "Access Granted" overlay for ACCESS_GRANTED_DISPLAY_MS, reload
   * form data in parallel, then acquire the now-unclaimed lock and flip
   * lockStatus to "locked". Idempotent — multiple call sites (SignalR push,
   * ClaimStuck response, reconcile-on-reconnect) can fire concurrently
   * without double-scheduling.
   */
  startGrantedTransition: () => void;
  /**
   * Reset the idempotency guard. Call when lockStatus leaves "requesting"
   * so a future re-request gets a clean transition.
   */
  resetIdempotency: () => void;
}

/**
 * Shared granted-transition driver. Three independent paths can trigger
 * "we just became the lock holder" and they all need to converge on the
 * same Access-Granted → reload → acquire → setLockStatus("locked") sequence:
 *
 *   1. SignalR `requestResolved {Granted}` push (the normal case)
 *   2. `claimStuckRequest` response (when the holder vanishes and we
 *      self-heal at countdown=0 — SignalR push may not have reached us)
 *   3. `reconcileAfterReconnect` (server says we hold the lock, but our UI
 *      is still in `requesting` because the original push fired during a
 *      disconnect window)
 *
 * The ref guard makes it idempotent so a race between paths doesn't
 * double-schedule the timeout and double-acquire.
 */
export function useGrantTransition(
  formIdRef: RefObject<string | null>,
  sessionIdRef: RefObject<string>,
  onLockGrantedRef: RefObject<(() => Promise<void>) | undefined>,
): GrantTransition {
  const setLockStatus = useLockStore((s) => s.setLockStatus);

  const startedRef = useRef(false);
  // Tracks the pending grant-transition timeout so we can cancel it on unmount.
  // Without this, navigating away during the 2s overlay would still fire the
  // setTimeout callback, which then attempts acquireLock + setLockStatus on a
  // stale closure — usually a no-op but a real risk of triggering state
  // updates against an unmounted store consumer.
  const timeoutRef = useRef<number | null>(null);

  const startGrantedTransition = useCallback(() => {
    if (startedRef.current) return;
    if (!formIdRef.current) return;
    startedRef.current = true;

    // Fire the form-reload in parallel with the 2s overlay so fresh data is
    // ready by the time we transition. The acquire-then-set-locked chain
    // awaits the reload to guarantee the form only renders once with the
    // latest values.
    const reloadPromise = onLockGrantedRef.current
      ? onLockGrantedRef.current().catch((err) => {
          eventBus.publish("error", { error: asError(err), context: "useGrantTransition.reloadAfterGrant" });
        })
      : Promise.resolve();

    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      if (!formIdRef.current) return;
      lockApi.acquireLock(formIdRef.current, sessionIdRef.current)
        .then(async (r) => {
          if (r.acquired) {
            await reloadPromise;
            setLockStatus("locked");
          }
        })
        .catch((err) => {
          eventBus.publish("error", { error: asError(err), context: "useGrantTransition.acquireAfterGrant" });
        });
    }, ACCESS_GRANTED_DISPLAY_MS);
  }, [setLockStatus, formIdRef, sessionIdRef, onLockGrantedRef]);

  const resetIdempotency = useCallback(() => {
    startedRef.current = false;
  }, []);

  // Cancel any pending grant transition on unmount so the delayed acquire
  // doesn't fire against a torn-down store consumer.
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  return { startGrantedTransition, resetIdempotency };
}
